import React from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';

const attendanceData = [
  { day: 'Day 1 (Thu)', date: 'Dec 24', attendees: 1200, diasporaReturns: 350 },
  { day: 'Day 2 (Fri)', date: 'Dec 25', attendees: 2800, diasporaReturns: 800 },
  { day: 'Day 3 (Sat)', date: 'Dec 26', attendees: 5100, diasporaReturns: 1450 },
  { day: 'Day 4 (Sun)', date: 'Dec 27', attendees: 8400, diasporaReturns: 2300 },
  { day: 'Day 5 (Mon)', date: 'Dec 28', attendees: 14200, diasporaReturns: 4100 },
  { day: 'Day 6 (Tue)', date: 'Dec 29', attendees: 24500, diasporaReturns: 7800 },
  { day: 'Day 7 (Wed)', date: 'Dec 30', attendees: 38000, diasporaReturns: 12500 },
];

const resourceData = [
  { resource: 'Joint Security Patrols', capacity: 500, utilized: 480, unit: 'Personnel' },
  { resource: 'Medical Tents & Ambulances', capacity: 25, utilized: 22, unit: 'Units' },
  { resource: 'VIP Royal Pavilions', capacity: 1200, utilized: 1150, unit: 'Seats' },
  { resource: 'Shuttle Buses (Jattu-Auchi)', capacity: 40, utilized: 38, unit: 'Buses' },
  { resource: 'Festival Food Courts', capacity: 60, utilized: 58, unit: 'Stalls' },
];

const raffleRevenueData = [
  { tier: 'Mega Tier (SUV & Solar)', revenue: 8500000, ticketsSold: 1700, color: '#D4AF37' },
  { tier: 'Midi Tier (Cash Grants)', revenue: 5200000, ticketsSold: 2600, color: '#0F5132' },
  { tier: 'Mini Tier (Tablets & Devices)', revenue: 3100000, ticketsSold: 3100, color: '#1E3A8A' },
  { tier: 'Starter Tier (Consolation)', revenue: 1400000, ticketsSold: 2800, color: '#78350F' },
];

export const AdminAnalyticsDashboard: React.FC = () => {
  return (
    <div className="space-y-8 bg-white p-6 sm:p-8 rounded-3xl border border-gray-200 shadow-sm">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-100 pb-4">
        <div>
          <h3 className="text-xl font-serif font-black text-[#0F5132]">ERUE Festival 2026 Institutional Analytics & Telemetry</h3>
          <p className="text-xs text-gray-500">Real-time data visualization of attendance trends, resource allocation, and raffle revenue performance.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-3 py-1 bg-emerald-100 text-emerald-900 rounded-full text-xs font-bold font-mono">
            🟢 Live Telemetry Active
          </span>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 bg-gradient-to-br from-emerald-900 to-slate-900 text-white rounded-2xl shadow-sm space-y-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300">Total Projected Attendance</span>
          <h4 className="text-2xl font-serif font-black">94,200+</h4>
          <p className="text-[11px] text-emerald-200">Across 7 Festival Days in Jattu</p>
        </div>

        <div className="p-5 bg-gradient-to-br from-slate-900 to-amber-950 text-white rounded-2xl shadow-sm space-y-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300">Total Raffle Revenue</span>
          <h4 className="text-2xl font-serif font-black">₦18,200,000</h4>
          <p className="text-[11px] text-amber-200">10,200 Active Tickets Issued</p>
        </div>

        <div className="p-5 bg-gradient-to-br from-blue-950 to-slate-900 text-white rounded-2xl shadow-sm space-y-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300">Resource Operational Index</span>
          <h4 className="text-2xl font-serif font-black">96.4%</h4>
          <p className="text-[11px] text-blue-200">Optimal Security & Medical Coverage</p>
        </div>

        <div className="p-5 bg-gradient-to-br from-amber-950 to-slate-900 text-white rounded-2xl shadow-sm space-y-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-amber-300">Diaspora Return Delegations</span>
          <h4 className="text-2xl font-serif font-black">27 Chapters</h4>
          <p className="text-[11px] text-amber-200">Representing 14 Countries Worldwide</p>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        
        {/* Attendance Trends Chart */}
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 space-y-4">
          <div>
            <h4 className="text-sm font-bold text-gray-900 font-serif">📈 7-Day Festival Attendance & Diaspora Growth</h4>
            <p className="text-xs text-gray-500">Daily visitor volume and diaspora returnees across festival schedule.</p>
          </div>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={attendanceData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="day" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="attendees" name="Total Attendees" stroke="#0F5132" strokeWidth={3} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="diasporaReturns" name="Diaspora Returns" stroke="#D4AF37" strokeWidth={3} dot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Raffle Revenue Analysis Chart */}
        <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 space-y-4">
          <div>
            <h4 className="text-sm font-bold text-gray-900 font-serif">💰 Raffle Revenue & Ticket Sales by Tier</h4>
            <p className="text-xs text-gray-500">Financial distribution across Mega, Midi, Mini, and Starter raffle tiers.</p>
          </div>
          <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={raffleRevenueData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="tier" tick={{ fontSize: 9 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(val: any) => [`₦${Number(val).toLocaleString()}`, 'Revenue']} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="revenue" name="Tier Revenue (₦)" fill="#0F5132" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      {/* Resource Utilization Bar Chart */}
      <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 space-y-4">
        <div>
          <h4 className="text-sm font-bold text-gray-900 font-serif">🛡️ Festival Resource Allocation & Utilization Rate</h4>
          <p className="text-xs text-gray-500">Monitoring capacity vs. operational utilization for security, medical, transport, and venues.</p>
        </div>
        <div className="h-64 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={resourceData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis type="number" tick={{ fontSize: 10 }} />
              <YAxis dataKey="resource" type="category" width={160} tick={{ fontSize: 10 }} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="capacity" name="Total Capacity" fill="#94a3b8" radius={[0, 4, 4, 0]} />
              <Bar dataKey="utilized" name="Active Utilization" fill="#0F5132" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

    </div>
  );
};
